import { lazy, Suspense } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import CreatorFanData from './creatorFanData';
import UserCourseData from './userCourseData';
import { useProfileData } from '../../hooks/useProfileData';

const UserProfileSections = () => {
  const navigate = useNavigate();
  const { authUser: currentAuthUser } = useAuth();
  const { creatorProfileData: profileData, isLoadingProfile} = useProfileData();

  const {
    userId = '',
    userName = '',
    shortBio = '',
    introMessage,
  } = profileData || {};

  const isProfileAuthUser = currentAuthUser?.documentId === userId;

  const handleSendMessage = (inputStr) => {
    const messageToSend = inputStr || '';
    if (messageToSend?.trim()) {
      navigate(`/${userName}/chat`, {
        state: { message: messageToSend },
      });
    }
  };

  const FrankSortinoCustomProfileNav = lazy(() =>
    import('../customUIElements/FrankSortino').then((module) => ({
      default: module.FrankSortinoCustomProfileNav,
    }))
  );

  const isStanfordUrl = window.location.href.startsWith(
    'https://stanford.soopra.ai'
  );

  return (
    <Box
      width="100%"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        pt: 3,
        gap: 3,
        pb: 12,
      }}
    >
      {/* Short Bio Section */}
      {shortBio && (
        <Box>
          <Typography
            component="p"
            variant="body2"
            color="text.secondary"
          >
            Biography
          </Typography>
          <Typography
            component="p"
            variant="body2"
            color="text.primary"
            sx={{ paddingTop: 1.5 }}
          >
            {shortBio}
          </Typography>
        </Box>
      )}

      {
        ((userName === 'srinivasjaini' || userName === 'sortino-chat')
          && !isStanfordUrl) &&
        (
          <Suspense>
            <FrankSortinoCustomProfileNav userName={userName} />
          </Suspense>
        )
      }

      {/* User Fan List Section */}
      {
        !isLoadingProfile &&
          <Suspense fallback={<></>}>
            <CreatorFanData />
          </Suspense>
      }

      {/* Suggested Questions Section */}
      {typeof introMessage !== 'string' && introMessage?.chips ? (
        <Box
          width="100%"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            maxWidth: '100dvw',
          }}
          gap={1.5}
        >
          <Typography
            component="p"
            variant="body2"
            color="text.secondary"
            sx={{
              pb: 0.5,
              overflow: 'hidden',
            }}
            textOverflow="ellipsis"
            whiteSpace="nowrap"
          >
            Suggested Questions
          </Typography>
          {introMessage.chips.map((val, ind) => (
            <Box
              component="a"
              key={`suggested-question-${ind}`}
              sx={{
                width: '100%',
                px: 3,
                py: 1.5,
                border: 1,
                borderRadius: (theme) => theme.shape.borderRadiusLg,
                borderColor: (theme) => theme.palette.divider,
                cursor: 'pointer',
                textDecoration: 'none',
              }}
              href={`/${userName}/chat`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleSendMessage(val);
              }}
            >
              <Typography variant="body2" color="text.primary">
                {val}
              </Typography>
            </Box>
          ))}
        </Box>
      ) : (
        !!isProfileAuthUser && (
          <Box
            width="100%"
            sx={{
              maxWidth: '100dvw',
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              fontWeight={500}
              sx={{ mb: 1 }}
            >
              Suggested Questions
            </Typography>
            <Box
              sx={{
                textAlign: 'center',
                p: 3,
                backgroundColor: 'background.default',
                borderRadius: 2,
                border: '1px dashed rgba(0, 0, 0, 0.2)',
              }}
            >
              <Typography variant="h7" fontWeight={600} sx={{ mb: 1 }}>
                Create a suggested question for people who visit your profile.
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 3 }}
              >
                Visitors can click the suggested question to immediately start
                interacting with your AI persona.
              </Typography>
              <Button
                variant="contained"
                size="large"
                sx={{
                  backgroundColor: 'grey.800',
                  color: 'white',
                  borderRadius: '8px',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: 'grey.900',
                  },
                }}
                onClick={(e) => {
                  e.preventDefault();
                  navigate(`/${currentAuthUser?.userName}/update/introduction`);
                }}
              >
                Create Suggested Question
              </Button>
            </Box>
          </Box>
        )
      )}

      {/* Course Data Section */}
      <Box width="100%">
        <UserCourseData
          authUser={currentAuthUser}
          profileData={profileData}
        />
      </Box>

    </Box>
  );
};

export default UserProfileSections;
